import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Zap, Rocket, Bell, Check, ShieldCheck, Star, ArrowRight, Lock } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import TopSpotCard from "@/components/TopSpotCard";
import { FadeUp } from "@/components/Reveal";
import { toast } from "sonner";
import BoostPricingModal from "@/components/BoostPricingModal";

const REGIONS = ["US", "UK", "India", "Global"];

function Pill({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap font-mono2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 border transition-all duration-200 ${
        active
          ? "border-amber-400 bg-amber-400/15 text-amber-700 shadow-[0_0_14px_rgba(245,158,11,0.18)]"
          : "border-slate-200 text-slate-700 bg-white hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50/50"
      }`}
    >
      {children}
    </button>
  );
}

const pillRowCls = "flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible";

export default function TopSpot() {
  const [params, setParams] = useSearchParams();
  const region = params.get("region") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    q.set("boosted", "1");
    if (region) q.set("region", region);
    api
      .get(`/companies?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [region]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <main
      data-testid="top-spot-page"
      className="pt-28 md:pt-16 min-h-screen"
      style={{ background: "linear-gradient(160deg, #FFFBF0 0%, #FFFFFF 40%, #F8FFF8 100%)" }}
    >
      <Seo
        title="Top Spot — HighRank"
        siteName="HighRank"
        description="Boosted businesses on HighRank — every listing with an active Boost, across all categories and regions."
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-amber-100">
        {/* Subtle gold orb */}
        <div className="absolute -top-16 right-12 w-72 h-72 rounded-full bg-amber-400/10 blur-[90px] pointer-events-none" />
        <div className="absolute top-4 left-1/3 w-48 h-48 rounded-full bg-orange-300/8 blur-[70px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="grid lg:grid-cols-[1fr_auto] lg:gap-8 items-stretch">
            <div className="flex flex-col">
              <FadeUp>
                <div className="inline-flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.35em] text-amber-600 mb-2">
                  <Zap size={10} className="text-amber-500" />
                  Boosted visibility
                </div>
                <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-900 flex items-center gap-3 mb-2">
                  <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-[0_4px_18px_rgba(245,158,11,0.35)] shrink-0">
                    <Crown size={20} className="text-white sm:hidden" />
                    <Crown size={24} className="text-white hidden sm:block" />
                  </span>
                  Top Spot
                </h1>
                <p className="max-w-2xl text-sm sm:text-base text-slate-600 leading-relaxed">
                  Every business with an active{" "}
                  <span className="text-amber-600 font-semibold">Boost</span>, across all categories and regions.
                  Boost controls Top Spot placement only — it never changes leaderboard order or the{" "}
                  <span className="text-emerald-600 font-semibold">HighRank Score</span>.
                </p>
              </FadeUp>

              {/* Region filters — pushed to bottom on desktop to fill the gap under the title */}
              <div className="mt-4 lg:mt-auto lg:pt-4">
                <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-slate-600 mb-1.5">Region</p>
                <div className={pillRowCls} data-testid="topspot-region-filters">
                  <Pill active={!region} onClick={() => update("region", "")} testId="topspot-region-all">
                    All regions
                  </Pill>
                  {REGIONS.map((r) => (
                    <Pill key={r} active={region === r} onClick={() => update("region", r)} testId={`topspot-region-${r.toLowerCase()}`}>
                      {r}
                    </Pill>
                  ))}
                </div>
                <p data-testid="topspot-count" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-2">
                  {loading ? "Loading…" : `${data?.total ?? 0} boosted listing${data?.total === 1 ? "" : "s"}`}
                </p>
              </div>
            </div>

            {/* Right-side moving preview — hidden on mobile */}
            <div className="hidden lg:block">
              <ScrollingPreview />
            </div>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-14">
        <div className="space-y-3" data-testid="topspot-list">
          {!loading && data?.items?.length === 0 && (
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white px-6 py-6 sm:px-8 sm:py-8 text-center shadow-[0_2px_18px_rgba(251,191,36,0.08)]">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center mx-auto mb-3 shadow-[0_4px_16px_rgba(245,158,11,0.30)]">
                <Crown size={20} className="text-white" />
              </div>
              <p className="font-heading text-lg font-semibold text-slate-800 mb-1.5">No boosted listings yet.</p>
              <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto leading-relaxed">
                Boost a listing to appear here — it controls Top Spot placement only, and never changes
                leaderboard order or HighRank Score.
              </p>
              <Link
                to="/leaderboard"
                data-testid="topspot-empty-cta"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm rounded-full px-5 py-2 transition-colors shadow-[0_2px_14px_rgba(245,158,11,0.28)]"
              >
                Browse the leaderboard
              </Link>
            </div>
          )}
          {data?.items?.map((c, i) => (
            <TopSpotCard key={c.id} company={c} index={i} />
          ))}
        </div>
      </section>

      {/* ── Pricing Section ─────────────────────────────── */}
      <PricingSection />
    </main>
  );
}

function ScrollingPreview() {
  const [topSpotItems, setTopSpotItems] = useState([]);
  const [leaderboardItems, setLeaderboardItems] = useState([]);

  useEffect(() => {
    api.get("/companies?boosted=1").then((r) => setTopSpotItems((r.data.items || []).slice(0, 3))).catch(() => {});
    api.get("/companies").then((r) => setLeaderboardItems((r.data.items || []).slice(0, 3))).catch(() => {});
  }, []);

  const toCard = (c, i, prime) => ({
    key: `${c.slug}-${i}`,
    rank: i + 1,
    name: c.name,
    tag: c.tagline || c.category,
    score: c.highrank_score ?? 0,
    region: c.region,
    logo: c.logo_url,
    prime,
  });

  const groups = [];
  if (topSpotItems.length) {
    groups.push({ label: "Top Spot", icon: Crown, cards: topSpotItems.map((c, i) => toCard(c, i, i === 0)) });
  }
  if (leaderboardItems.length) {
    groups.push({ label: "Leaderboard", icon: Star, cards: leaderboardItems.map((c, i) => toCard(c, i, false)) });
  }
  if (groups.length === 0) return null;

  // Duplicate the whole group set for a seamless infinite scroll loop
  const doubled = [...groups, ...groups];

  return (
    <div
      className="relative w-[340px] h-[420px] rounded-3xl p-[1.5px]"
      style={{
        background: "linear-gradient(160deg, #F59E0B, #FB923C, #10B981, #06B6D4)",
        boxShadow: "0 24px 64px -16px rgba(245,158,11,0.28), 0 12px 28px -8px rgba(16,185,129,0.15), 0 0 0 1px rgba(251,191,36,0.10)",
      }}
      aria-hidden="true"
    >
    <div
      className="relative w-full h-full rounded-[calc(1.5rem-1.5px)] overflow-hidden"
      style={{
        background: "linear-gradient(155deg, #FFF7ED 0%, #FFFBEB 20%, #FFFFFF 50%, #ECFDF5 75%, #F0FDFA 100%)",
      }}
    >
      {/* Decorative colour orbs */}
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-400/20 blur-[40px] pointer-events-none" />
      <div className="absolute bottom-4 -left-6 w-28 h-28 rounded-full bg-emerald-400/20 blur-[36px] pointer-events-none" />
      <div className="absolute top-1/2 right-2 w-20 h-20 rounded-full bg-orange-300/15 blur-[28px] pointer-events-none" />

      {/* Fade masks top + bottom */}
      <div className="absolute top-0 left-0 right-0 h-14 z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, #FFFBEB, rgba(255,251,235,0.6), transparent)" }} />
      <div className="absolute bottom-0 left-0 right-0 h-14 z-10 pointer-events-none" style={{ background: "linear-gradient(to top, #F0FDFA, rgba(240,253,250,0.6), transparent)" }} />

      {/* Header label */}
      <div className="absolute top-3 left-4 right-4 flex items-center justify-between z-20 pointer-events-none">
        <div className="inline-flex items-center gap-1.5 rounded-full backdrop-blur-md border border-amber-300/60 px-2.5 py-1" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.92), rgba(255,251,235,0.92))" }}>
          <Crown size={10} className="text-amber-500" />
          <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-amber-700 font-bold">Live · Real listings</span>
        </div>
        <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)] animate-pulse" />
      </div>

      {/* Scrolling stack */}
      <motion.div
        className="absolute inset-x-0 top-0 flex flex-col gap-3 px-4 pt-14"
        animate={{ y: ["0%", "-50%"] }}
        transition={{ duration: 22, ease: "linear", repeat: Infinity }}
      >
        {doubled.map((group, gi) => (
          <div key={`${group.label}-${gi}`} className="flex flex-col gap-3">
            <GroupLabel icon={group.icon} label={group.label} />
            {group.cards.map((m) => (
              <MockCard key={`${m.key}-${gi}`} m={m} />
            ))}
          </div>
        ))}
      </motion.div>
    </div>
    </div>
  );
}

function GroupLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 px-0.5">
      <Icon size={10} className="text-slate-400" />
      <span className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function MockCard({ m }) {
  const isPrime = !!m.prime;
  return (
    <div
      className={`relative rounded-xl border p-3 flex items-center gap-3 transition-all ${
        isPrime
          ? "border-amber-300 bg-gradient-to-br from-amber-50 via-white to-orange-50/40 shadow-[0_4px_16px_rgba(251,191,36,0.20)]"
          : "border-slate-200/80 bg-gradient-to-br from-white to-slate-50/60 shadow-[0_2px_10px_rgba(0,0,0,0.05)]"
      }`}
    >
      {isPrime && <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl bg-gradient-to-b from-amber-400 via-orange-400 to-amber-500" />}
      <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
        isPrime
          ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-[0_2px_8px_rgba(245,158,11,0.3)]"
          : m.rank <= 3
            ? "bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-[0_2px_6px_rgba(16,185,129,0.25)]"
            : "bg-slate-100 text-slate-600"
      }`}>
        {m.rank}
      </div>
      {m.logo ? (
        <img
          src={m.logo}
          alt={`${m.name} logo`}
          className="shrink-0 w-8 h-8 rounded-lg object-cover border border-slate-200 bg-white"
        />
      ) : (
        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 text-slate-500 font-heading font-bold text-sm">
          {m.name?.charAt(0)?.toUpperCase() || "?"}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-heading text-sm font-semibold text-slate-900 truncate">{m.name}</p>
          {isPrime && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/40 px-1.5 py-0.5">
              <Zap size={8} className="text-amber-600" />
              <span className="font-mono2 text-[8px] uppercase tracking-wider text-amber-700">Boosted</span>
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 truncate mt-0.5">{m.tag}</p>
      </div>
      <div className="shrink-0 flex flex-col items-end">
        <span className="font-mono2 text-[16px] font-bold leading-none" style={{ color: m.score >= 90 ? "#059669" : m.score >= 85 ? "#10B981" : "#14B8A6" }}>{m.score}</span>
        <span className="font-mono2 text-[8px] uppercase tracking-wider text-slate-400 mt-1">{m.region}</span>
      </div>
    </div>
  );
}

function PricingSection() {
  const [pricingOpen, setPricingOpen] = useState(false);
  const [activeTier, setActiveTier] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);

  const openTier = (tier) => { setActiveTier(tier); setPricingOpen(true); };

  const handleNotify = async (e) => {
    e.preventDefault();
    setNotifyBusy(true);
    try {
      await api.post("/boost/notify-me", { email: notifyEmail });
      setNotifySent(true);
      toast.success("You're on the list!");
    } catch {
      toast.error("Something went wrong — try again.");
    } finally {
      setNotifyBusy(false);
    }
  };

  return (
    <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

      {/* ── Header ── */}
      <FadeUp className="text-center mb-3">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-600 mb-3">Visibility Upgrades</p>
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-900">
          Optional Boosts
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto leading-relaxed">
          Discover listings are always free. These are optional visibility upgrades —
          none of them affect your HighRank Score or Rank Position.
        </p>
      </FadeUp>

      {/* ── Three-tier pricing cards ── */}
      <div className="mt-8 grid lg:grid-cols-3 gap-6 items-stretch">

        {/* ─ Tier 1: Discover Boost · $4.99/7 days ─ */}
        <FadeUp delay={0.05} className="flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <div className="px-7 pt-7 pb-0 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest text-slate-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1">
                <Zap size={9} /> Starter
              </span>
            </div>

            <h3 className="font-display text-2xl uppercase tracking-wide text-slate-900 mb-1">Discover Boost</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-5xl font-black text-slate-900 leading-none">$4.99</span>
              <span className="text-sm text-slate-400 self-end mb-1">for 7 days</span>
            </div>

            {/* Options */}
            <div className="space-y-2 mb-6">
              {[
                { label: "Option A", desc: "Sponsored Discover page slot — social post or product video" },
                { label: "Option B", desc: "Leaderboard video placement — video upload" },
              ].map(({ label, desc }) => (
                <div key={label} className="flex gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                  <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={10} className="text-slate-500" />
                  </span>
                  <div>
                    <p className="font-mono2 text-[9px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
                    <p className="text-xs text-slate-600 leading-snug">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-slate-400 flex items-start gap-1.5 mb-7">
              <ShieldCheck size={11} className="shrink-0 mt-0.5" />
              Sponsored tag always visible. Never affects Rank Position.
            </p>
          </div>

          <div className="px-7 pb-7">
            <button
              data-testid="discover-boost-cta"
              onClick={() => openTier("discover_boost")}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold text-sm rounded-full py-3.5 transition-all duration-200 group"
            >
              Get Started <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </FadeUp>

        {/* ─ Tier 2: Boost · $9.99/10 days ─ */}
        <FadeUp delay={0.1} className="flex flex-col rounded-2xl border-2 border-slate-800 bg-white overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.10)] hover:shadow-[0_6px_32px_rgba(0,0,0,0.16)] transition-shadow">
          <div className="h-1.5 bg-gradient-to-r from-slate-700 to-slate-900" />
          <div className="px-7 pt-6 pb-0 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                <Star size={9} /> Growth
              </span>
            </div>

            <h3 className="font-display text-2xl uppercase tracking-wide text-slate-900 mb-1">Boost</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-5xl font-black text-slate-900 leading-none">$9.99</span>
              <span className="text-sm text-slate-400 self-end mb-1">for 10 days</span>
            </div>

            <ul className="space-y-3 mb-6 flex-1">
              {[
                "Top Spot page placement",
                "2× sponsored Discover slots (bundled)",
                "Leaderboard video placement",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={10} className="text-slate-600" />
                  </span>
                  <span className="text-sm text-slate-700">{f}</span>
                </li>
              ))}
            </ul>

            <p className="text-[11px] text-slate-400 flex items-start gap-1.5 mb-7">
              <ShieldCheck size={11} className="shrink-0 mt-0.5" />
              Boosted tag always visible. Never affects Rank Position.
            </p>
          </div>

          <div className="px-7 pb-7">
            <button
              data-testid="boost-cta"
              onClick={() => openTier("boost")}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold text-sm rounded-full py-3.5 transition-all duration-200 group"
            >
              Get Started <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </FadeUp>

        {/* ─ Tier 3: Pro Boost · $14.99/10 days — MOST POPULAR (light premium) ─ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative flex flex-col rounded-2xl border-2 border-emerald-500 bg-white overflow-hidden shadow-[0_8px_40px_rgba(16,185,129,0.20)] hover:shadow-[0_12px_48px_rgba(16,185,129,0.30)] transition-shadow lg:scale-[1.03] lg:origin-center"
        >
          {/* Top gradient bar */}
          <div className="h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-500" />

          {/* Most popular badge */}
          <div className="absolute top-0 right-6 translate-y-3">
            <span className="inline-flex items-center gap-1 bg-emerald-500 text-white font-black text-[9px] uppercase tracking-widest rounded-full px-3 py-1.5 shadow-lg">
              <Rocket size={9} /> Most Popular
            </span>
          </div>

          <div className="px-7 pt-8 pb-0 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                <Crown size={9} /> Pro
              </span>
            </div>

            <h3 className="font-display text-2xl uppercase tracking-wide text-slate-900 mb-1">Pro Boost</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-5xl font-black text-emerald-600 leading-none">$14.99</span>
              <span className="text-sm text-slate-400 self-end mb-1">for 10 days</span>
            </div>

            <ul className="space-y-3 mb-6 flex-1">
              {[
                "Everything in Boost",
                "Home screen video placement",
                "HighRank share card — branded shareable graphic",
                "Pro Alumni status — permanent badge + priority access",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={10} className="text-emerald-600" />
                  </span>
                  <span className="text-sm text-slate-700 font-medium">{f}</span>
                </li>
              ))}
            </ul>

            <p className="text-[11px] text-slate-500 flex items-start gap-1.5 mb-7">
              <ShieldCheck size={11} className="shrink-0 mt-0.5 text-emerald-500" />
              Boosted tag always visible. Never affects Rank Position.
            </p>
          </div>

          <div className="px-7 pb-7">
            <button
              data-testid="pro-boost-cta"
              onClick={() => openTier("pro_boost")}
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-full py-3.5 transition-all duration-200 shadow-[0_4px_20px_rgba(16,185,129,0.35)] group"
            >
              Get Started <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </motion.div>

      </div>

      {/* ── Stripe trust badge ── */}
      <div className="flex items-center justify-center gap-1.5 mt-8 opacity-40">
        <Lock size={11} className="text-slate-400" />
        <span className="text-[11px] text-slate-400 tracking-wide">Payments powered by</span>
        <svg viewBox="0 0 512 214" className="h-[15px] w-auto" aria-label="Stripe">
          <path fill="#635bff" d="M512 110.08c0-36.409-17.636-65.138-51.342-65.138-33.85 0-54.33 28.73-54.33 64.854 0 42.808 24.179 64.426 58.88 64.426 16.925 0 29.725-3.84 39.396-9.244v-28.445c-9.67 4.836-20.764 7.823-34.844 7.823-13.796 0-26.027-4.836-27.591-21.618h69.547c0-1.85.284-9.245.284-12.658m-70.258-13.511c0-16.071 9.814-22.756 18.774-22.756 8.675 0 17.92 6.685 17.92 22.756zm-90.31-51.627c-13.939 0-22.899 6.542-27.876 11.094l-1.85-8.818h-31.288v165.83l35.555-7.537.143-40.249c5.12 3.698 12.657 8.96 25.173 8.96 25.458 0 48.64-20.48 48.64-65.564-.142-41.245-23.609-63.716-48.498-63.716m-8.534 97.991c-8.391 0-13.37-2.986-16.782-6.684l-.143-52.765c3.698-4.124 8.818-6.968 16.925-6.968 12.942 0 21.902 14.506 21.902 33.137 0 19.058-8.818 33.28-21.902 33.28M241.493 36.551l35.698-7.68V0l-35.698 7.538zm0 10.809h35.698v124.444h-35.698zm-38.257 10.524L200.96 47.36h-30.72v124.444h35.556V87.467c8.39-10.951 22.613-8.96 27.022-7.396V47.36c-4.551-1.707-21.191-4.836-29.582 10.524m-71.112-41.386-34.702 7.395-.142 113.92c0 21.05 15.787 36.551 36.836 36.551 11.662 0 20.195-2.133 24.888-4.693V140.8c-4.55 1.849-27.022 8.391-27.022-12.658V77.653h27.022V47.36h-27.022zM35.982 83.484c0-5.546 4.551-7.68 12.09-7.68 10.808 0 24.461 3.272 35.27 9.103V51.484c-11.804-4.693-23.466-6.542-35.27-6.542C19.2 44.942 0 60.018 0 85.192c0 39.252 54.044 32.995 54.044 49.92 0 6.541-5.688 8.675-13.653 8.675-11.804 0-26.88-4.836-38.827-11.378v33.849c13.227 5.689 26.596 8.106 38.827 8.106 29.582 0 49.92-14.648 49.92-40.106-.142-42.382-54.329-34.845-54.329-50.774" />
        </svg>
      </div>

      <BoostPricingModal
        open={pricingOpen}
        onOpenChange={(v) => { setPricingOpen(v); if (!v) setActiveTier(null); }}
        company={null}
        initialTier={activeTier}
      />
    </section>
  );
}

